import { Company } from './company'
import { Option } from './option'
import { User } from './user'

// 契約タイプ: 0=フル機能, 1=巡察記録のみ
export const CONTRACT_TYPE = {
  FULL: 0,
  INSTRUCTION_RECORD_ONLY: 1,
} as const

export type ContractType = (typeof CONTRACT_TYPE)[keyof typeof CONTRACT_TYPE]

export const CONTRACT_TYPE_OPTIONS: Option[] = [
  {
    label: 'フル機能',
    value: CONTRACT_TYPE.FULL,
  },
  {
    label: '巡察記録のみ',
    value: CONTRACT_TYPE.INSTRUCTION_RECORD_ONLY,
  },
]

export interface CompanyBranchRole {
  id: string
  name: string
  permissions: number
}

export interface CompanyBranch {
  id?: string
  companyId?: string
  branchName: string
  contractType?: ContractType
  prefecture: number
  address: string
  postalCode?: string
  phoneNumber?: string
  faxNumber?: string
  invoiceStampName?: string
  bankNumber?: string
  bankBranchName?: string
  bankBranchNumber?: string
  bankName?: string
  bankCode?: string
  note?: string
  plugins?: number[]
  company?: Company
  users?: User[]
  roles?: CompanyBranchRole[]
}

export const PLUGIN = {
  EXPORT_FOR_OBIC: 1 << 0,
  REWVIEW: 1 << 1,
  TRAVEL_COST: 1 << 2,
  SECURITY_REPORT_MUST: 1 << 3,
  SECURITY_BEGIN_CONDITION: 1 << 4,
  PLUGIN_TIPS_FROM_STRAYA: 1 << 5,
  DATA_TRACKING: 1 << 6,
  GMO_SOKKYU_INTEGRATION: 1 << 7,
  EXTERNAL_API: 1 << 9, // 外部API連携
  VOICE_INPUT: 1 << 10, // 音声入力プラン
}

export const PLUGIN_OPTIONS: Option[] = [
  {
    label: 'オービック給料連携用csv出力',
    value: PLUGIN.EXPORT_FOR_OBIC,
  },
  {
    label: 'アンケート',
    value: PLUGIN.REWVIEW,
  },
  {
    label: '警備報告書必須',
    value: PLUGIN.SECURITY_REPORT_MUST,
  },
  {
    label: '交通費計算',
    value: PLUGIN.TRAVEL_COST,
  },
  {
    label: '出勤前体調報告',
    value: PLUGIN.SECURITY_BEGIN_CONDITION,
  },
  {
    label: 'strayaからのヒントを表示',
    value: PLUGIN.PLUGIN_TIPS_FROM_STRAYA,
  },
  {
    label: 'データトラッキング',
    value: PLUGIN.DATA_TRACKING,
  },
  {
    label: 'GMO即給連携',
    value: PLUGIN.GMO_SOKKYU_INTEGRATION,
  },
  {
    label: '外部API連携',
    value: PLUGIN.EXTERNAL_API,
  },
  {
    label: '音声入力プラン',
    value: PLUGIN.VOICE_INPUT,
  },
]

export enum CompanyBranchRolePermission {
  SalaryView = 0, // 給与の閲覧権限
  SalaryEdit = 1, // 給与の編集権限
  BillingView = 2, // 請求の閲覧権限
  BillingEdit = 3, // 請求の編集権限
  InspectionView = 4, // 巡察の閲覧権限
  InspectionEdit = 5, // 巡察の編集権限
}

export const CompanyBranchRolePermissionBits = {
  [CompanyBranchRolePermission.SalaryView]: 1 << 0, // 1
  [CompanyBranchRolePermission.SalaryEdit]: 1 << 1, // 2
  [CompanyBranchRolePermission.BillingView]: 1 << 2, // 4
  [CompanyBranchRolePermission.BillingEdit]: 1 << 3, // 8
  [CompanyBranchRolePermission.InspectionView]: 1 << 4, // 16
  [CompanyBranchRolePermission.InspectionEdit]: 1 << 5, // 32
} as const

export const newCompanyBranchRolePermissions = (
  n: number,
): CompanyBranchRolePermission[] => {
  const permissions: CompanyBranchRolePermission[] = []
  for (const [permission, bit] of Object.entries(
    CompanyBranchRolePermissionBits,
  )) {
    if ((n & bit) !== 0) {
      permissions.push(Number(permission) as CompanyBranchRolePermission)
    }
  }
  return permissions
}

export const permissionsToValue = (
  permissions: CompanyBranchRolePermission[],
): number => {
  let value = 0
  for (const permission of permissions) {
    value |= CompanyBranchRolePermissionBits[permission]
  }
  return value
}

export const hasPermission = (
  permissions: CompanyBranchRolePermission[],
  permission: CompanyBranchRolePermission,
): boolean => {
  return permissions.includes(permission)
}

export const canViewSalary = (
  permissions: CompanyBranchRolePermission[],
): boolean => {
  return hasPermission(permissions, CompanyBranchRolePermission.SalaryView)
}

export const canEditSalary = (
  permissions: CompanyBranchRolePermission[],
): boolean => {
  return hasPermission(permissions, CompanyBranchRolePermission.SalaryEdit)
}

export const canViewBilling = (
  permissions: CompanyBranchRolePermission[],
): boolean => {
  return hasPermission(permissions, CompanyBranchRolePermission.BillingView)
}

export const canEditBilling = (
  permissions: CompanyBranchRolePermission[],
): boolean => {
  return hasPermission(permissions, CompanyBranchRolePermission.BillingEdit)
}

export const allCompanyBranchRolePermissions =
  (): CompanyBranchRolePermission[] => {
    return [
      CompanyBranchRolePermission.SalaryView,
      CompanyBranchRolePermission.SalaryEdit,
      CompanyBranchRolePermission.BillingView,
      CompanyBranchRolePermission.BillingEdit,
    ]
  }
